import { FormWithSchema } from "Inputs/FormTools";
import { useState } from "react";
import { useCounterKey } from "Tools";
import { AnySchema } from "yup";

export interface ModalState {
  isVisible: boolean;
  open: () => void;
  close: () => void;
  onCancel: () => unknown;
  key: number;
}

interface ModalStateOptions {
  onOpen?: () => void;
  onClose?: () => void;
}

export function useModalState(options?: ModalStateOptions): ModalState {
  const [isVisible, setVisible] = useState(false);
  const [key, bumpKey] = useCounterKey();
  const open = () => {
    setVisible(true);
    options?.onOpen?.();
  };
  const close = () => {
    setVisible(false);
    bumpKey(); // Clear modal
    options?.onClose?.();
  };

  return {
    isVisible,
    open,
    close,
    onCancel: close,
    key,
  };
}

export function useModalFormState<S extends AnySchema>(
  options: ModalStateOptions & {
    form: FormWithSchema<S>;
  },
): ModalState & {
  submitFormAndClose: () => Promise<void>;
} {
  const [isVisible, setVisible] = useState(false);
  const [key, bumpKey] = useCounterKey();
  const open = () => {
    options.form.updateInitialArgsAndReset();
    setVisible(true);
    options?.onOpen?.();
  };
  const forceClose = () => {
    setVisible(false);
    bumpKey(); // Clear modal
    options?.onClose?.();
  };
  const close = () => {
    if (options.form.dirty === true) {
      const shouldClose = window.confirm(
        "Close this window?  Your changes will not be saved.",
      );

      if (!shouldClose) {
        return;
      }
    }

    forceClose();
  };

  const submitFormAndClose = async () => {
    if (!options.form.isValid) {
      return;
    }
    await options.form.submitForm();
    forceClose();
  };

  return {
    isVisible,
    open,
    close,
    onCancel: close,
    key,
    submitFormAndClose,
  };
}
